import { useEffect, useState } from 'react';
import { ArrowDownTrayIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useAttachmentDownloadUrl } from '../../hooks/useAttachmentDownloadUrl';
import { ImagePreviewThumb } from '../common/ImagePreviewThumb';
import { ImageLightbox } from '../common/ImageLightbox';
import type { CommentAttachmentDTO } from '../../services/api/comments';

/**
 * Phase 25 Plan 25-05 — D-20 attachment row for existing comments.
 *
 * Image MIMEs (jpeg/png/gif/webp) auto-mint a signed URL on mount so the
 * thumbnail can render inline. Other MIMEs (pdf, docx, …) defer the mint
 * until click — keeps the initial list-load light when comments carry many
 * non-image attachments.
 *
 * Once minted for a non-image, the URL is opened in a new tab via
 * window.open(..., 'noopener,noreferrer'). Image links use a regular
 * <a target=_blank rel=noopener noreferrer> so the user can click through to
 * the full-size image in a new tab.
 *
 * Quick task 260810-lpq (LPQ-01/LPQ-03) — every row also exposes a separate
 * explicit download control (a sibling button, never nested inside the
 * anchor) that mints a SECOND, save-forcing signed URL independent of the
 * view/click action. Image thumbnails render via the shared
 * `ImagePreviewThumb`, which reveals a larger hover preview.
 *
 * MKS-274 — the image chip is a button that opens the attachment full size in
 * an `ImageLightbox` overlay instead of an anchor to the signed URL; the
 * overlay keeps a link to the original. The download control and non-image
 * rows are unchanged.
 */
const IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

export interface AttachmentRowProps {
  taskId: number;
  commentId: number;
  attachments: CommentAttachmentDTO[];
}

export function AttachmentRow({ taskId, commentId, attachments }: AttachmentRowProps) {
  if (attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachments.map((a) => (
        <AttachmentItem key={a.id} taskId={taskId} commentId={commentId} attachment={a} />
      ))}
    </div>
  );
}

function AttachmentItem({
  taskId,
  commentId,
  attachment,
}: {
  taskId: number;
  commentId: number;
  attachment: CommentAttachmentDTO;
}) {
  const isImage = IMAGE_MIMES.has(attachment.contentType);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [downloadEnabled, setDownloadEnabled] = useState(false);

  // Image thumbnails are eager so the thumb can render on first paint.
  // Non-images are lazy: enable on click. The hook is gated on `enabled`
  // and won't fire until that flips.
  const enabled = isImage || downloadEnabled;
  const { data, isFetching } = useAttachmentDownloadUrl(
    taskId,
    commentId,
    attachment.id,
    enabled,
  );

  // After mint completes for non-images, open the URL in a new tab and reset
  // the local enabled flag so a follow-up click re-mints (URL TTL is 1h, but
  // we'd rather not over-eagerly persist a state that hasn't been used).
  useEffect(() => {
    if (!isImage && downloadEnabled && data?.url) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
      setDownloadEnabled(false);
    }
  }, [isImage, downloadEnabled, data?.url]);

  // Quick task 260810-lpq (LPQ-01) — explicit download control, a SECOND,
  // independent mint requesting the save-forcing disposition. Sibling of the
  // view/click chip below, never nested inside its anchor.
  const [saveEnabled, setSaveEnabled] = useState(false);
  const { data: saveData } = useAttachmentDownloadUrl(
    taskId,
    commentId,
    attachment.id,
    saveEnabled,
    'attachment',
  );
  useEffect(() => {
    if (saveEnabled && saveData?.url) {
      window.open(saveData.url, '_blank', 'noopener,noreferrer');
      setSaveEnabled(false);
    }
  }, [saveEnabled, saveData?.url]);

  const sizeKb = (attachment.sizeBytes / 1024).toFixed(1);

  const downloadButton = (
    <button
      type="button"
      onClick={() => setSaveEnabled(true)}
      aria-label={`Download ${attachment.filename}`}
      data-testid={`attachment-download-${attachment.id}`}
      className="inline-flex items-center justify-center w-6 h-6 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
    >
      <ArrowDownTrayIcon className="w-4 h-4" aria-hidden />
    </button>
  );

  // MKS-274 — the chip opens the lightbox overlay instead of linking off to
  // the signed URL in a new tab.
  if (isImage) {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => {
            if (data?.url) setLightboxOpen(true);
          }}
          className="inline-flex items-center gap-2 px-2 py-1 rounded-lg border border-gray-200 bg-gray-50 text-xs hover:bg-gray-100"
          title={attachment.filename}
          data-testid={`attachment-open-${attachment.id}`}
        >
          <ImagePreviewThumb src={data?.url} alt={attachment.filename} />
          <span className="font-medium text-gray-800 truncate max-w-[160px]">
            {attachment.filename}
          </span>
        </button>
        {downloadButton}
        {lightboxOpen && data?.url && (
          <ImageLightbox
            src={data.url}
            alt={attachment.filename}
            onClose={() => setLightboxOpen(false)}
            testId={`attachment-lightbox-${attachment.id}`}
          />
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          if (!downloadEnabled) setDownloadEnabled(true);
        }}
        className="inline-flex items-center gap-2 px-2 py-1 rounded-lg border border-gray-200 bg-gray-50 text-xs hover:bg-gray-100"
        aria-busy={isFetching || undefined}
      >
        <PaperClipIcon className="w-4 h-4 text-gray-500" aria-hidden />
        <span className="font-medium text-gray-800 truncate max-w-[160px]">
          {attachment.filename}
        </span>
        <span className="text-gray-500">{sizeKb} KB</span>
      </a>
      {downloadButton}
    </span>
  );
}
